import type { ComponentType, SVGProps } from 'react';
import {
  HomeIcon,
  ShoppingBagIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  PaintBrushIcon,
  BuildingStorefrontIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline';

export interface SellerNavItem {
  name: string;
  href: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Short label for compact mobile contexts */
  shortName?: string;
  /** When true, subpaths do not count as active (e.g. /seller/store vs /seller/store/performance) */
  exact?: boolean;
}

export interface SellerNavSection {
  id: 'shop';
  label?: string;
  items: SellerNavItem[];
}

export const SELLER_DESIGN_STUDIO_HREF = '/seller/design-tool';

/** External full-screen editor — launched from sidebar footer, not in-app routing */
export const SELLER_DESIGN_STUDIO_LAUNCHER = {
  href: SELLER_DESIGN_STUDIO_HREF,
  label: 'Open Design Studio',
  icon: PaintBrushIcon,
} as const;

export const SELLER_SETTINGS_HREF = '/seller/settings';
export const SELLER_NOTIFICATIONS_HREF = '/seller/notifications';

export const SELLER_NAV_SECTIONS: SellerNavSection[] = [
  {
    id: 'shop',
    label: 'Your shop',
    items: [
      { name: 'Overview', shortName: 'Home', href: '/seller/dashboard', icon: HomeIcon },
      { name: 'My store', href: '/seller/store', icon: BuildingStorefrontIcon },
      { name: 'Designs', href: '/seller/designs', icon: ShoppingBagIcon },
      { name: 'Orders', href: '/seller/orders', icon: ClipboardDocumentListIcon },
      { name: 'Analytics', href: '/seller/analytics', icon: ChartBarIcon },
      {
        name: 'Earnings & payouts',
        shortName: 'Earnings',
        href: '/seller/earnings',
        icon: CurrencyDollarIcon,
      },
      {
        name: 'Seller progress',
        shortName: 'Progress',
        href: '/seller/progress',
        icon: PresentationChartLineIcon,
      },
    ],
  },
];

/** Flat list for page titles */
export const SELLER_NAVIGATION: SellerNavItem[] = SELLER_NAV_SECTIONS.flatMap(
  (section) => section.items,
);

export function isSellerNavActive(
  pathname: string,
  href: string,
  exact = false,
): boolean {
  if (exact) {
    return pathname === href || pathname === `${href}/`;
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function getSellerPageTitle(pathname: string): string {
  if (isSellerNavActive(pathname, SELLER_SETTINGS_HREF)) {
    return 'Settings';
  }
  if (pathname.startsWith('/seller/wallet-guide')) {
    return 'Wallet guide';
  }
  if (pathname.startsWith('/seller/notifications')) {
    return 'Notifications';
  }
  if (pathname.startsWith('/seller/progress')) {
    return 'Seller progress';
  }
  if (pathname.startsWith('/seller/store/performance')) {
    return 'Store performance';
  }
  if (pathname.startsWith('/seller/store/conversion')) {
    return 'Store conversion';
  }
  if (isSellerNavActive(pathname, '/seller/store')) {
    return 'My store';
  }
  return SELLER_NAVIGATION.find((item) => isSellerNavActive(pathname, item.href, item.exact))?.name ?? 'Seller Portal';
}
